import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import * as XLSX from 'xlsx';
import { documentApi, extractionApi, DocumentUrls, LegendData, LegendEntry } from '../api';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  docId: string;
  docName: string;
}

type ViewerTab = 'pdf' | 'image' | 'legend' | 'excel' | 'aidata';

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  docId,
  docName,
}) => {
  const [activeTab, setActiveTab] = useState<ViewerTab>('pdf');
  const [urls, setUrls] = useState<DocumentUrls | null>(null);
  const [legend, setLegend] = useState<LegendData | null>(null);
  const [aiData, setAiData] = useState<any>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && docId) {
      loadData();
    }
  }, [isOpen, docId]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    const imageUrl = urls?.image_url;

    setImagePreviewUrl(null);
    if (!imageUrl) {
      return undefined;
    }

    documentApi.resolveImageUrl(imageUrl)
      .then(({ url, revoke }) => {
        if (cancelled) {
          if (revoke) window.URL.revokeObjectURL(url);
          return;
        }
        objectUrl = revoke ? url : null;
        setImagePreviewUrl(url);
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn('Could not load document image:', e);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [urls?.image_url]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [urlsData, legendData] = await Promise.all([
        extractionApi.getDocumentUrls(docId),
        extractionApi.getLegend(),
      ]);
      setUrls(urlsData);
      setLegend(legendData);

      // Try to fetch AI data if URL exists
      if (urlsData.ai_data_url) {
        try {
          const response = await fetch(urlsData.ai_data_url);
          if (response.ok) {
            const data = await response.json();
            setAiData(data);
          }
        } catch (e) {
          console.warn('Could not fetch AI data from S3:', e);
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load document data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tabs: { id: ViewerTab; label: string; available: boolean }[] = [
    { id: 'pdf', label: 'PDF', available: !!urls?.pdf_url },
    { id: 'image', label: 'Image', available: !!urls?.image_url },
    { id: 'legend', label: 'Legend', available: true },
    { id: 'excel', label: 'Excel', available: !!urls?.excel_url },
    { id: 'aidata', label: 'AI Data', available: !!urls?.ai_data_url || !!aiData },
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Document Viewer</h2>
            <span style={styles.docName}>{docName}</span>
          </div>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.activeTab : {}),
                ...(tab.available ? {} : styles.disabledTab),
              }}
              onClick={() => tab.available && setActiveTab(tab.id)}
              disabled={!tab.available}
            >
              {tab.label}
              {!tab.available && <span style={styles.unavailableBadge}>N/A</span>}
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {loading && (
            <div style={styles.loadingState}>Loading document data...</div>
          )}

          {error && (
            <div style={styles.errorState}>{error}</div>
          )}

          {!loading && !error && (
            <>
              {activeTab === 'pdf' && urls?.pdf_url && (
                <PdfViewer pdfUrl={urls.pdf_url} />
              )}

              {activeTab === 'image' && urls?.image_url && imagePreviewUrl && (
                <div style={styles.imageContainer}>
                  <img
                    src={imagePreviewUrl}
                    alt={docName}
                    style={styles.image}
                  />
                </div>
              )}

              {activeTab === 'legend' && legend && (
                <LegendView legend={legend} />
              )}

              {activeTab === 'excel' && urls?.excel_url && (
                <ExcelViewer excelUrl={urls.excel_url} />
              )}

              {activeTab === 'aidata' && (
                <AIDataView aiData={aiData} aiDataUrl={urls?.ai_data_url} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// PDF Viewer Component with zoom and page navigation
const PdfViewer: React.FC<{ pdfUrl: string }> = ({ pdfUrl }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF. The file may be inaccessible.');
  };

  const goToPrevPage = () => setPageNumber((prev) => Math.max(1, prev - 1));
  const goToNextPage = () => setPageNumber((prev) => Math.min(numPages, prev + 1));
  const zoomIn = () => setScale((prev) => Math.min(3.0, prev + 0.25));
  const zoomOut = () => setScale((prev) => Math.max(0.5, prev - 0.25));
  const resetZoom = () => setScale(1.0);

  if (pdfError) {
    return (
      <div style={pdfStyles.errorContainer}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
        <p>{pdfError}</p>
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={pdfStyles.downloadLink}>
          Open PDF in new tab
        </a>
      </div>
    );
  }

  return (
    <div style={pdfStyles.container}>
      {/* Controls */}
      <div style={pdfStyles.controls}>
        <div style={pdfStyles.pageControls}>
          <button
            style={pdfStyles.controlButton}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            ◀
          </button>
          <span style={pdfStyles.pageInfo}>
            Page {pageNumber} of {numPages || '...'}
          </span>
          <button
            style={pdfStyles.controlButton}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          >
            ▶
          </button>
        </div>
        <div style={pdfStyles.zoomControls}>
          <button style={pdfStyles.controlButton} onClick={zoomOut} disabled={scale <= 0.5}>
            −
          </button>
          <button style={pdfStyles.zoomLabel} onClick={resetZoom}>
            {Math.round(scale * 100)}%
          </button>
          <button style={pdfStyles.controlButton} onClick={zoomIn} disabled={scale >= 3.0}>
            +
          </button>
        </div>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={pdfStyles.externalLink}
        >
          ↗ Open
        </a>
      </div>

      {/* PDF Document */}
      <div style={pdfStyles.documentContainer}>
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<div style={pdfStyles.loading}>Loading PDF...</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>
      </div>
    </div>
  );
};

const pdfStyles: Record<string, React.CSSProperties> = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#374151',
    borderRadius: 8,
    overflow: 'hidden',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#1f2937',
    borderBottom: '1px solid #4b5563',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  zoomControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  controlButton: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    backgroundColor: '#4b5563',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  pageInfo: {
    color: 'white',
    fontSize: 13,
    minWidth: 100,
    textAlign: 'center',
  },
  zoomLabel: {
    padding: '6px 12px',
    fontSize: 12,
    backgroundColor: '#4b5563',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    minWidth: 60,
  },
  externalLink: {
    fontSize: 13,
    color: '#93c5fd',
    textDecoration: 'none',
  },
  documentContainer: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    padding: 20,
  },
  loading: {
    color: 'white',
    padding: 40,
    textAlign: 'center',
  },
  errorContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#374151',
    borderRadius: 8,
    color: 'white',
    padding: 40,
  },
  downloadLink: {
    marginTop: 16,
    padding: '10px 20px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: 6,
    textDecoration: 'none',
  },
};

// Excel Viewer Component
const ExcelViewer: React.FC<{ excelUrl: string }> = ({ excelUrl }) => {
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [sheetData, setSheetData] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExcel();
  }, [excelUrl]);

  useEffect(() => {
    if (workbook && activeSheet) {
      const sheet = workbook.Sheets[activeSheet];
      const data = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
      setSheetData(data);
    }
  }, [workbook, activeSheet]);

  const loadExcel = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(excelUrl);
      if (!response.ok) throw new Error('Failed to fetch Excel file');
      
      const arrayBuffer = await response.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: 'array' });
      
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      setActiveSheet(wb.SheetNames[0] || '');
    } catch (e: any) {
      console.error('Excel load error:', e);
      setError(e.message || 'Failed to load Excel file');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={excelStyles.loadingContainer}>
        <div style={excelStyles.spinner}>⏳</div>
        <p>Loading Excel file...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={excelStyles.errorContainer}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
        <p>{error}</p>
        <a href={excelUrl} target="_blank" rel="noopener noreferrer" style={excelStyles.downloadLink}>
          Download Excel File
        </a>
      </div>
    );
  }

  return (
    <div style={excelStyles.container}>
      {/* Sheet Tabs */}
      <div style={excelStyles.sheetTabs}>
        {sheetNames.map((name) => (
          <button
            key={name}
            style={{
              ...excelStyles.sheetTab,
              ...(activeSheet === name ? excelStyles.activeSheetTab : {}),
            }}
            onClick={() => setActiveSheet(name)}
          >
            {name}
            <span style={excelStyles.rowCount}>
              {workbook?.Sheets[name] ? 
                (XLSX.utils.sheet_to_json(workbook.Sheets[name]).length) + ' rows' : ''}
            </span>
          </button>
        ))}
        <a
          href={excelUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={excelStyles.downloadButton}
        >
          ⬇ Download
        </a>
      </div>

{/* Data Table */}
      <div style={excelStyles.tableContainer}>
        {sheetData.length === 0 ? (
          <div style={excelStyles.emptySheet}>No data in this sheet</div>
        ) : (() => {
          // Calculate max columns across all rows
          const maxCols = Math.max(...sheetData.map(row => row?.length || 0), 0);
          const colIndices = Array.from({ length: maxCols }, (_, i) => i);
          
          // Get merge information
          const sheet = workbook?.Sheets[activeSheet];
          const merges: XLSX.Range[] = sheet?.['!merges'] || [];
          
          // Build a map of cells that are covered by merges
          // Key: "row,col" -> { skip: boolean, rowSpan?: number, colSpan?: number }
          const mergeMap = new Map<string, { skip: boolean; rowSpan?: number; colSpan?: number }>();
          
          merges.forEach((merge) => {
            const startRow = merge.s.r;
            const startCol = merge.s.c;
            const endRow = merge.e.r;
            const endCol = merge.e.c;
            const rowSpan = endRow - startRow + 1;
            const colSpan = endCol - startCol + 1;
            
            // Mark the start cell with span info
            mergeMap.set(`${startRow},${startCol}`, { skip: false, rowSpan, colSpan });
            
            // Mark all other cells in the merge as skip
            for (let r = startRow; r <= endRow; r++) {
              for (let c = startCol; c <= endCol; c++) {
                if (r !== startRow || c !== startCol) {
                  mergeMap.set(`${r},${c}`, { skip: true });
                }
              }
            }
          });
          
          const getMergeInfo = (rowIdx: number, colIdx: number) => {
            return mergeMap.get(`${rowIdx},${colIdx}`);
          };
          
          return (
            <table style={excelStyles.table}>
              <thead>
                <tr>
                  <th style={excelStyles.rowNumberHeader}>#</th>
                  {colIndices.map((colIdx) => (
                    <th key={colIdx} style={excelStyles.headerCell}>
                      {getColumnLetter(colIdx)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheetData.map((row, rowIdx) => (
                  <tr key={rowIdx} style={rowIdx % 2 === 1 ? excelStyles.evenRow : {}}>
                    <td style={excelStyles.rowNumber}>{rowIdx + 1}</td>
                    {colIndices.map((colIdx) => {
                      const mergeInfo = getMergeInfo(rowIdx, colIdx);
                      
                      // Skip cells that are covered by a merge
                      if (mergeInfo?.skip) {
                        return null;
                      }
                      
                      const cellStyle = rowIdx === 0 
                        ? excelStyles.dataHeaderCell 
                        : excelStyles.cell;
                      
                      return (
                        <td
                          key={colIdx}
                          style={cellStyle}
                          rowSpan={mergeInfo?.rowSpan}
                          colSpan={mergeInfo?.colSpan}
                        >
                          {row?.[colIdx] ?? ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  );
};

// Helper to convert column index to Excel letter (0 -> A, 1 -> B, etc.)
const getColumnLetter = (index: number): string => {
  let letter = '';
  while (index >= 0) {
    letter = String.fromCharCode((index % 26) + 65) + letter;
    index = Math.floor(index / 26) - 1;
  }
  return letter;
};

const excelStyles: Record<string, React.CSSProperties> = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  sheetTabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb',
    overflowX: 'auto',
  },
  sheetTab: {
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderBottom: 'none',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
  },
  activeSheetTab: {
    backgroundColor: '#059669',
    color: 'white',
    borderColor: '#059669',
  },
  rowCount: {
    fontSize: 10,
    opacity: 0.7,
  },
  downloadButton: {
    marginLeft: 'auto',
    padding: '6px 12px',
    fontSize: 12,
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: 6,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    maxWidth: '100%',
  },
  table: {
    borderCollapse: 'collapse',
    fontSize: 12,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    minWidth: 'max-content',
  },
  rowNumberHeader: {
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    textAlign: 'center',
    color: '#9ca3af',
    fontWeight: 500,
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 3,
    minWidth: 40,
  },
  headerCell: {
    padding: '6px 12px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    borderRight: '1px solid #f3f4f6',
    textAlign: 'center',
    color: '#9ca3af',
    fontWeight: 500,
    fontSize: 10,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  dataHeaderCell: {
    padding: '10px 12px',
    backgroundColor: '#f3f4f6',
    borderBottom: '2px solid #d1d5db',
    borderRight: '1px solid #e5e7eb',
    textAlign: 'left',
    fontWeight: 600,
    color: '#1f2937',
    position: 'sticky',
    top: 28,
    zIndex: 2,
  },
  rowNumber: {
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRight: '1px solid #e5e7eb',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 11,
    position: 'sticky',
    left: 0,
    zIndex: 1,
  },
  cell: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    borderRight: '1px solid #f3f4f6',
    color: '#374151',
    whiteSpace: 'nowrap',
    minWidth: 80,
  },
  evenRow: {
    backgroundColor: '#fafafa',
  },
  loadingContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
  },
  spinner: {
    fontSize: 48,
    marginBottom: 16,
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    padding: 40,
  },
  downloadLink: {
    marginTop: 16,
    padding: '10px 20px',
    backgroundColor: '#059669',
    color: 'white',
    borderRadius: 6,
    textDecoration: 'none',
  },
  emptySheet: {
    padding: 40,
    textAlign: 'center',
    color: '#9ca3af',
  },
};

// Legend Tab Component
const LegendView: React.FC<{ legend: LegendData }> = ({ legend }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const filteredSymbols = legend.symbols.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.section.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedSymbols = filteredSymbols.reduce((acc, symbol) => {
    if (!acc[symbol.section]) acc[symbol.section] = [];
    acc[symbol.section].push(symbol);
    return acc;
  }, {} as Record<string, LegendEntry[]>);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div style={styles.legendContainer}>
      <input
        type="text"
        placeholder="Search symbols..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={styles.searchInput}
      />
      <div style={styles.legendList}>
        {Object.entries(groupedSymbols).map(([category, symbols]) => (
          <div key={category} style={styles.legendCategory}>
            <div
              style={styles.categoryHeader}
              onClick={() => toggleCategory(category)}
            >
              <span>{expandedCategories.has(category) ? '▼' : '▶'}</span>
              <span style={styles.categoryName}>{category}</span>
              <span style={styles.categoryCount}>{symbols.length}</span>
            </div>
            {expandedCategories.has(category) && (
              <div style={styles.categorySymbols}>
                {symbols.map((symbol, idx) => (
                  <div key={idx} style={styles.symbolItem}>
                    <span style={styles.symbolName}>{symbol.name}</span>
                    {symbol.notes && (
                      <span style={styles.symbolNotes}>{symbol.notes}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// AI Data Tab Component
const AIDataView: React.FC<{ aiData: any; aiDataUrl: string | null | undefined }> = ({
  aiData,
  aiDataUrl,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['equipment_in_scope', 'instruments', 'lines'])
  );

  if (!aiData && !aiDataUrl) {
    return (
      <div style={styles.emptyState}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
        <p>No AI extraction data available.</p>
        <p style={{ fontSize: 12, color: '#9ca3af' }}>
          Run AI extraction or upload AI data JSON.
        </p>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const renderSection = (key: string, data: any) => {
    const isExpanded = expandedSections.has(key);
    const isArray = Array.isArray(data);
    const count = isArray ? data.length : null;

    return (
      <div key={key} style={styles.aiSection}>
        <div
          style={styles.aiSectionHeader}
          onClick={() => toggleSection(key)}
        >
          <span>{isExpanded ? '▼' : '▶'}</span>
          <span style={styles.aiSectionName}>{key}</span>
          {count !== null && (
            <span style={styles.aiSectionCount}>{count} items</span>
          )}
        </div>
        {isExpanded && (
          <div style={styles.aiSectionContent}>
            <pre style={styles.jsonPre}>
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.aiDataContainer}>
      {aiDataUrl && (
        <div style={styles.aiDataHeader}>
          <a
            href={aiDataUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.downloadLink}
          >
            Download Raw JSON
          </a>
        </div>
      )}
      {aiData && (
        <div style={styles.aiDataSections}>
          {Object.entries(aiData).map(([key, value]) =>
            renderSection(key, value)
          )}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  },
  modal: {
    width: '95%',
    maxWidth: 1200,
    height: '90vh',
    backgroundColor: 'white',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: '#1f2937',
  },
  docName: {
    fontSize: 13,
    color: '#6b7280',
  },
  closeButton: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    color: '#6b7280',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
    padding: '0 24px',
    gap: 4,
  },
  tab: {
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 500,
    color: '#6b7280',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    marginBottom: -1,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  activeTab: {
    color: '#2563eb',
    borderBottomColor: '#2563eb',
  },
  disabledTab: {
    color: '#d1d5db',
    cursor: 'not-allowed',
  },
  unavailableBadge: {
    fontSize: 10,
    padding: '2px 4px',
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    color: '#9ca3af',
  },
  content: {
    flex: 1,
    padding: 24,
    overflow: 'auto',
    backgroundColor: '#f9fafb',
  },
  loadingState: {
    textAlign: 'center',
    color: '#6b7280',
    padding: 40,
  },
  errorState: {
    textAlign: 'center',
    color: '#dc2626',
    padding: 40,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
  },
  imageContainer: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    backgroundColor: '#374151',
    borderRadius: 8,
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  legendContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  searchInput: {
    padding: '12px 16px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    marginBottom: 16,
    outline: 'none',
  },
  legendList: {
    flex: 1,
    overflow: 'auto',
  },
  legendCategory: {
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#f9fafb',
  },
  categoryName: {
    flex: 1,
  },
  categoryCount: {
    fontSize: 12,
    color: '#6b7280',
    backgroundColor: '#e5e7eb',
    padding: '2px 8px',
    borderRadius: 12,
  },
  categorySymbols: {
    padding: '8px 16px 16px',
    borderTop: '1px solid #e5e7eb',
  },
  symbolItem: {
    padding: '6px 0',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  symbolName: {
    fontSize: 13,
    color: '#1f2937',
  },
  symbolNotes: {
    fontSize: 11,
    color: '#9ca3af',
  },
  aiDataContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  aiDataHeader: {
    marginBottom: 16,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  downloadLink: {
    fontSize: 13,
    color: '#2563eb',
    textDecoration: 'none',
  },
  aiDataSections: {
    flex: 1,
    overflow: 'auto',
  },
  aiSection: {
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  aiSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#f9fafb',
    fontFamily: 'monospace',
  },
  aiSectionName: {
    flex: 1,
    color: '#7c3aed',
  },
  aiSectionCount: {
    fontSize: 12,
    color: '#6b7280',
  },
  aiSectionContent: {
    padding: 16,
    borderTop: '1px solid #e5e7eb',
    maxHeight: 400,
    overflow: 'auto',
  },
  jsonPre: {
    margin: 0,
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#374151',
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    color: '#6b7280',
  },
};

export default DocumentViewer;
